import {
  Checkbox,
  FormControlLabel,
  IconButton,
  TextField,
  Typography,
  Chip,
  Box,
} from "@mui/material";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import RecurrenceDialog from "../taskInputs/recurringTasks/RecurrenceDialog";
import { useMemo, useState } from "react";
import { dispatch } from "../../../redux/store";
import {
  removeRecurringTask,
  updateTaskFrequency,
} from "../../../redux/actions/recurringTasks";
import { setOverlayVisible } from "../../../redux/actions/overlay";
import dayjs from "dayjs";
import useRecurringTasksAPIClient from "../../../api/recurringTasks/hooks/useRecurringTasksAPIClient";
import * as jsonpatch from "fast-json-patch";

const RecurringTask = ({ task, taskIsCurrentlyInBacklog }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const { setTaskRecurringFrequency, removeTaskFrequency } =
    useRecurringTasksAPIClient();
  const {
    intervalCount,
    intervalDateType,
    startDate,
    startIntervalOnCompletion,
  } = task.frequency;

  const recurringLabelText = useMemo(() => {
    if (startIntervalOnCompletion) {
      return `${intervalCount} ${intervalDateType}${intervalCount > 1 ? "s" : ""} after completion`;
    }
    return `Every${intervalCount === 1 ? "" : ` ${intervalCount}`} ${intervalDateType}${intervalCount > 1 ? "s" : ""}`;
  }, [intervalCount, intervalDateType, startIntervalOnCompletion]);

  const nextRecurrence = useMemo(() => {
    if (!startDate) return null;

    if (startIntervalOnCompletion) {
      // For "after completion" tasks, the countdown starts from the last completion (stored in startDate)
      // If task is completed, show when it will reappear
      if (task.completed) {
        return dayjs(startDate)
          .add(intervalCount, intervalDateType)
          .format("dddd, D/M/YYYY");
      }
      // If task is in backlog (not completed), it's already due
      return "Due now";
    }

    // For fixed schedule tasks, calculate the next occurrence from the anchor date
    const now = dayjs();
    const start = dayjs(startDate);

    // Find the next occurrence that's in the future
    const diff = now.diff(start, intervalDateType, true);
    const intervalsSinceStart = Math.ceil(diff / intervalCount);
    const nextDate = start.add(
      intervalsSinceStart * intervalCount,
      intervalDateType,
    );

    return nextDate.format("dddd, D/M/YYYY");
  }, [
    startDate,
    intervalCount,
    intervalDateType,
    startIntervalOnCompletion,
    task.completed,
  ]);

  return (
    <div className="recurring-task">
      <div className={`recurring-task_header`}>
        <Typography variant="h4NoMargin" className={`recurring-task_title`}>
          {task.name}
        </Typography>
      </div>
      <div className={`recurring-task_body`}>
        <div className={`recurring-task_controls-container`}>
          <FormControlLabel
            label=""
            control={
              <Checkbox
                label="Recurring"
                checked={true}
                onChange={() => {
                  dispatch(
                    setOverlayVisible({
                      visible: true,
                      overlayType: "confirm",
                      confirmationText:
                        "Do you want to stop this task from recurring?",
                      confirmCallback: async () => {
                        dispatch(removeRecurringTask(task.taskId));
                        await removeTaskFrequency(parseInt(task.taskId));
                        dispatch(
                          setOverlayVisible({
                            visible: false,
                            overlayType: "",
                          }),
                        );
                      },
                    }),
                  );
                }}
              />
            }
          />
          <TextField
            label="Recurs"
            variant="outlined"
            onClick={() => {
              setDialogOpen(true);
            }}
            value={recurringLabelText}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <IconButton className={"recurring-tasks-container_icon-button"}>
                  <EventRepeatIcon />
                </IconButton>
              ),
            }}
          />
          <RecurrenceDialog
            open={dialogOpen}
            onClose={() => setDialogOpen(false)}
            frequency={task.frequency}
            setFrequency={async (frequency) => {
              const patches = jsonpatch.compare(task.frequency, frequency);
              dispatch(updateTaskFrequency(task.taskId, frequency));
              await setTaskRecurringFrequency(parseInt(task.taskId), patches);
            }}
          />
        </div>
        <div className={`recurring-task_status-container`}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              mt: { xs: 1, md: 0 },
            }}
          >
            <Chip
              size="small"
              label={
                startIntervalOnCompletion
                  ? "After completion"
                  : "Fixed schedule"
              }
              sx={{
                bgcolor: startIntervalOnCompletion ? "#e3f2fd" : "#e8f5e9",
                color: startIntervalOnCompletion ? "#1565c0" : "#2e7d32",
                fontSize: "0.7rem",
              }}
            />
          </Box>
          <Typography
            variant="body2"
            sx={{ mb: 0, textAlign: "center", mt: 1 }}
          >
            {taskIsCurrentlyInBacklog
              ? "This task is waiting to be picked up"
              : `Next recurs on: ${nextRecurrence}`}
          </Typography>
        </div>
      </div>
    </div>
  );
};

export default RecurringTask;
